// src/components/mdx/client/visualizations/AxisIntegrityDemo.client.tsx

/**
 * Demonstrates how truncated Y-axes exaggerate small changes in marketing metrics.
 */

'use client'

import { useMemo, useState, useTransition } from 'react'

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from '~/lib/charts/client'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { getOrCreateTooltipWrapper, type CustomTooltipProps } from '~/lib/charts/tooltip'
import { AXIS_TOKENS, MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BLOG_CHART_THROTTLE_MS, BlogChartPlotArea, BlogChartTooltipShell, getBlogChartMargin } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { generateAxisIntegrityData } from '~/components/mdx/client/visualizations/marketingDataVizData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'
import { Switch } from '~/components/ui/Switch'
import { Label } from '~/components/Label'

function AxisTooltip({ active, payload, label }: CustomTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null
  }
  const value = typeof payload[0]?.value === 'number' ? payload[0].value.toFixed(1) : payload[0]?.value
  return (
    <BlogChartTooltipShell title={label !== undefined ? String(label) : undefined}>
      <p className="text-gray-300">
        Revenue: <span className="font-semibold text-white">${value}k</span>
      </p>
    </BlogChartTooltipShell>
  )
}

interface AxisIntegrityDemoContentProps {
  isActive: boolean
}

function AxisIntegrityDemoContent({ isActive }: AxisIntegrityDemoContentProps): JSX.Element {
  const [truncateAxis, setTruncateAxis] = useState(true)
  const [, startTransition] = useTransition()
  const data = useMemo(() => generateAxisIntegrityData(), [])
  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const tooltip = useMemo(() => getOrCreateTooltipWrapper(AxisTooltip), [])
  const values = data.map(point => point.revenue)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const swing = ((max - min) / min) * 100
  const margin = getBlogChartMargin(isMobile)

  return (
    <BlogChartShell
      title="Y-Axis Integrity"
      description="The same weekly revenue series — honest scale vs truncated axis."
      gradient="from-rose-400 to-amber-400"
    >
      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
        <Switch
          id="truncate-axis"
          checked={truncateAxis}
          onCheckedChange={checked => {
            startTransition(() => {
              setTruncateAxis(checked)
            })
          }}
        />
        <Label htmlFor="truncate-axis" className="text-sm text-gray-200">
          Truncate Y-axis (starts at ${Math.round(min - 2)}k instead of $0)
        </Label>
      </div>

      <p className="mb-4 text-sm text-gray-300" aria-live="polite">
        Actual swing: <span className="font-semibold text-amber-300">{swing.toFixed(1)}%</span>
        {truncateAxis ? ' — looks like a crisis on a truncated axis.' : ' — looks stable on a zero-based axis.'}
      </p>

      <BlogChartPlotArea className="h-[360px] sm:h-[420px]">
        <ResponsiveContainer width="100%" height="100%" debounce={300} initialDimension={{ width: 800, height: 480 }}>
          <LineChart data={data} margin={margin} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
            <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" />
            <XAxis dataKey="week" stroke={AXIS_TOKENS.stroke} fontSize={isMobile ? 10 : 12} />
            <YAxis
              stroke={AXIS_TOKENS.stroke}
              fontSize={isMobile ? 10 : 12}
              domain={truncateAxis ? [min - 2, max + 2] : [0, max + 40]}
              tickFormatter={value => `$${value}k`}
            />
            <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
            <Line
              type="monotone"
              dataKey="revenue"
              name="Revenue"
              stroke={truncateAxis ? MARKETING_DATA_VIZ_SERIES.accent : MARKETING_DATA_VIZ_SERIES.mer}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              animationDuration={isActive && !prefersReducedMotion ? 600 : 0}
            />
          </LineChart>
        </ResponsiveContainer>
      </BlogChartPlotArea>
    </BlogChartShell>
  )
}

export default function AxisIntegrityDemo(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Y-axis integrity comparison demo">
      {({ isIntersecting }) => (
        <AxisIntegrityDemoContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
